import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  Container,
} from "reactstrap";
import Loader from "../../../Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

// RTK Query hooks - adjust import path / names if your slice uses different names
import {
  useGetTagsByIdQuery,
  useCreateTagsMutation,
  useUpdateTagsMutation,
  useGetTagsQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface TagPayload {
  name: string;
}

interface TagEditProps {
  tagId?: string | number;
  compact?: boolean; // when true, omit breadcrumb/page wrapper (for modal)
  onSaved?: () => void; // optional callback parent can use to close modal / refresh
}

const TagEditView: React.FC<TagEditProps> = ({
  tagId,
  compact = true,
  onSaved,
}) => {
  const isEditMode = tagId !== undefined && tagId !== null;
  const tagIdStr = isEditMode ? String(tagId) : undefined;

  const [saveError, setSaveError] = useState<string | null>(null);

  // fetch tag detail when editing (skip when no id)
  const {
    data: tagData,
    isFetching: fetchingTag,
    refetch: refetchTag,
  } = useGetTagsByIdQuery(tagIdStr as string, { skip: !isEditMode });

  // to refresh list after save
  const { refetch: refetchTags } = useGetTagsQuery(undefined);

  const [createTag, { isLoading: creating }] = useCreateTagsMutation();
  const [updateTag, { isLoading: updating }] = useUpdateTagsMutation();

  const formik = useFormik<TagPayload>({
    enableReinitialize: true,
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter tag name"),
    }),
    onSubmit: async (values) => {
      setSaveError(null);
      try {
        if (isEditMode && tagId !== undefined) {
          const idNumber = Number(tagId);
          const idToSend = Number.isNaN(idNumber) ? tagIdStr : idNumber;
          // update API shape assumed { id, values } or (id, values) depending on slice - adjust if needed
          await updateTag({ id: idToSend, values }).unwrap();
        } else {
          await createTag(values).unwrap();
        }

        // refresh list and single item
        refetchTags();
        if (isEditMode) refetchTag();

        if (onSaved) onSaved();
      } catch (err: any) {
        const message =
          err?.data?.message || err?.message || "Failed to save tag";
        console.error("Save failed:", err);
        setSaveError(String(message));
      }
    },
  });

  // populate form when editing
  useEffect(() => {
    if (isEditMode && tagData) {
      formik.setValues({
        name: tagData.name ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData, isEditMode]);

  // show loader if editing and the single item is loading (compact = modal)
  if (isEditMode && fetchingTag && compact) {
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  }

  // Full page wrapper when not compact (similar look to your earlier full-page TagEditView)
  if (!compact) {
    return (
      <div className="page-content">
        <Container fluid>
          {!compact && (
            <Row>
              <Col lg={8}>
                {saveError && <Alert color="danger">{saveError}</Alert>}
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {isEditMode ? "Edit Tag" : "Create Tag"}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                      }}
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="tag-name">
                          Tag Name
                        </Label>
                        <Input
                          id="tag-name"
                          name="name"
                          placeholder="Enter tag name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(formik.touched.name && formik.errors.name)
                          }
                        />
                        {formik.touched.name && formik.errors.name ? (
                          <div className="text-danger mt-1">
                            {formik.errors.name}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-end">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={creating || updating}
                        >
                          {creating || updating ? (
                            <>
                              <Spinner size="sm" className="me-1" /> Saving...
                            </>
                          ) : isEditMode ? (
                            "Update"
                          ) : (
                            "Create"
                          )}
                        </Button>
                      </div>
                    </form>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">Info</h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted">
                      Tags are simple labels assigned to products (e.g. "Office
                      Supplies"). Provide a unique name.
                    </p>
                    <hr />
                    <p className="mb-1">
                      <strong>API shape</strong>
                    </p>
                    <pre className="small p-2 bg-light rounded">{`{"name":"string"}`}</pre>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  }

  // Modal / compact UI
  return (
    <>
      {saveError && (
        <Row>
          <Col lg={12}>
            <Alert color="danger">{saveError}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label className="form-label" htmlFor="tag-name">
                    Tag Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="tag-name"
                    name="name"
                    placeholder="Enter tag name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div className="text-danger mt-1">{formik.errors.name}</div>
                  ) : null}
                </div>

                <div className="text-end">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={creating || updating}
                  >
                    {creating || updating ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Saving...
                      </>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default TagEditView;
